import PropTypes from 'prop-types';

import { useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import ButtonBase from '@mui/material/ButtonBase';
import Hidden from '@mui/material/Hidden';
import AppBar from '@mui/material/AppBar';

import IconButton from '@design-system/IconButton';

import MenuOutline from '@experimental-components/IconsComponents/MenuOutline';
import CloseOutline from '@experimental-components/IconsComponents/CloseOutline';
import ChevronLogo from '@experimental-components/IconsComponents/ChevronLogo';
import { ParrotLogo, ParrotLogoMark } from '@experimental-components/IconsComponents/ParrotLogo';

import { useSidebarLayout } from 'providers/root/SidebarLayoutProvider';
import useEnvironment from 'hooks/useEnvironment';

import { setCollapsePrincipalMenuKey } from 'utils/principalMenu';

import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const drawerWidth = 260;

const useStyles = makeStyles((theme) => ({
  drawer: {
    [theme.breakpoints.up('sm')]: {
      width: (props) => (props?.open ? drawerWidth : `64px`),
      flexShrink: 0,
    },
  },

  appBar: {
    background: (props) => (props.isTestEnv ? theme.palette.testEnv.main : theme.palette.common.black),
    [theme.breakpoints.up('sm')]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
  },

  menuButton: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      display: 'none',
    },
  },

  drawerPaperMdMobile: {
    zIndex: '1300',
    width: (props) => (props?.open ? drawerWidth : `64px`),
    backgroundColor: (props) => (props.isTestEnv ? theme.palette.testEnv.main : theme.palette.common.black),
    overflowX: 'hidden',
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: 200,
    }),
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.darkGrey[50]} ${theme.palette.common.black}`,
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'auto',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.darkGrey[50],
    },
  },
  headerMdMobile: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    // Open: logo left, collapse chevron right. Collapsed: the lone menu button stays centered.
    justifyContent: (props) => (props?.open ? 'space-between' : 'center'),
    flexDirection: 'row',
    gap: 16,
    minHeight: '5.5rem',
    maxHeight: '5.5rem',
    // No padding-top: the box is already taller than the logo and `alignItems: center` does the rest.
    padding: (props) => (props?.open ? '0 1.125rem 1.5rem' : '0 1rem 2rem'),
  },
  contentMenuMdMobile: {
    height: '100%',
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
  },

  drawerRootMobile: {
    zIndex: '1300',
  },

  drawerPaperMobile: {
    width: '100%',
    backgroundColor: (props) => (props.isTestEnv ? theme.palette.testEnv.main : theme.palette.common.black),
  },

  toolbarMobile: {
    justifyContent: 'space-between',
  },

  logo: {
    height: 50,
  },

  logSmall: {
    height: 35,
  },
}));

function SidebarLayoutSidebar({ children, window }) {
  const { isTestEnv } = useEnvironment();
  const history = useHistory();
  const { t } = useTranslation();
  const { openDrawer, setOpenDrawer, openMdDrawer, setOpenMdDrawer } = useSidebarLayout();

  const classes = useStyles({ isTestEnv, open: openMdDrawer });
  const theme = useTheme();

  const handleDrawerToggle = () => {
    setOpenDrawer(!openDrawer);
  };

  const handleDrawerMdToggle = () => {
    setOpenMdDrawer((state) => !state);
    setCollapsePrincipalMenuKey(!openMdDrawer);
  };

  function handleLogoClick() {
    history.push('/');
  }

  const container = window !== undefined ? () => window.document.body : undefined;

  function renderHeaderMdMenu() {
    if (!openMdDrawer) {
      return (
        <>
          <IconButton aria-label="open drawer" color="inherit" edge="start" onClick={handleDrawerMdToggle} size="large">
            <MenuOutline color={theme.palette.common.white} size={16} />
          </IconButton>
        </>
      );
    }

    return (
      <>
        <IconButton onClick={handleLogoClick} size="large">
          <ParrotLogo className={classes.logo} />
        </IconButton>
        <IconButton
          aria-label="open_drawer_menu"
          color="inherit"
          edge="start"
          onClick={handleDrawerMdToggle}
          size="large"
        >
          <ChevronLogo className={classes.chevronLeft} color={theme.palette.common.white} size={16} />
        </IconButton>
      </>
    );
  }

  return (
    <>
      <Hidden lgUp smUp>
        <AppBar className={classes.appBar} position="fixed">
          <Toolbar className={classes.toolbarMobile}>
            <IconButton
              aria-label="open drawer"
              className={classes.menuButton}
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              size="large"
            >
              <MenuOutline color={theme.palette.common.white} size={24} />
            </IconButton>

            <Box>
              <ParrotLogoMark className={classes.logSmall} />
            </Box>
          </Toolbar>
        </AppBar>
      </Hidden>

      <nav aria-label="mailbox folders" className={classes.drawer}>
        <Hidden implementation="css" smUp>
          <Drawer
            ModalProps={{
              keepMounted: true,
            }}
            anchor={theme.direction === 'rtl' ? 'right' : 'left'}
            classes={{
              root: classes.drawerRootMobile,
              paper: classes.drawerPaperMobile,
            }}
            container={container}
            onClose={handleDrawerToggle}
            open={openDrawer}
            variant="temporary"
          >
            <Box
              height="100%"
              maxHeight={theme.typography.pxToRem(100)}
              minHeight={theme.typography.pxToRem(100)}
              p={2}
            >
              {openDrawer && (
                <ButtonBase disableRipple onClick={handleDrawerToggle}>
                  <CloseOutline color={theme.palette.common.white} />
                </ButtonBase>
              )}
            </Box>
            {children}
          </Drawer>
        </Hidden>

        <Hidden implementation="css" smDown>
          <Drawer
            classes={{
              paper: classes.drawerPaperMdMobile,
            }}
            open
            variant="permanent"
          >
            <Box className={classes.headerMdMobile}>{renderHeaderMdMenu()}</Box>
            <Box className={classes.contentMenuMdMobile}>{children}</Box>
          </Drawer>
        </Hidden>
      </nav>
    </>
  );
}

SidebarLayoutSidebar.propTypes = {
  children: PropTypes.node,
  window: PropTypes.object,
};

export default SidebarLayoutSidebar;
